import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import axios from "axios";
import axiosInstant from "../../api";
import { TOKEN } from "../../store/constant";
import { AuthCard } from "../authV2/components/constants";
import { BasicPage } from "../authV2/components";

// Consent step of the MCP connector sign-in (API lib/controllers/oauth.js).
// claude.ai sends the browser to the API's /oauth/authorize, which validates
// the request and forwards it here with the same query string. A signed-in
// internal user approves or denies; the API answers with the exact URL to
// send the browser back to (with a one-time code, or an access_denied error).
// Approving gives the app a personal key that acts as this user; it shows up
// in Settings with the other keys and can be revoked there.

type ConsentInfo = {
  client_name: string;
  redirect_host: string;
  user: { id: number; name?: string; email?: string };
};

type Status = "loading" | "ready" | "submitting" | "forbidden" | "invalid" | "error";

const failureStatus = (httpStatus?: number): Status => {
  if (httpStatus === 403) return "forbidden";
  if (httpStatus === 400) return "invalid";
  return "error";
};

const MESSAGES: Record<"forbidden" | "invalid" | "error", string> = {
  forbidden: "Connecting apps is only available to internal team members.",
  invalid: "This connection link isn't valid. Start the connection again from the app.",
  error: "Something went wrong. Please try again.",
};

export function OAuthConsentPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("loading");
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    let cancelled = false;
    if (!localStorage.getItem(TOKEN)) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
      return () => {
        cancelled = true;
      };
    }
    axiosInstant
      .get(`/oauth/consent${window.location.search}`)
      .then((res) => {
        if (cancelled) return;
        setInfo(res.data);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        const httpStatus = err?.response?.status;
        // 401 is handled by the API client (logout -> login -> back here).
        if (httpStatus === 401) return;
        setStatus(failureStatus(httpStatus));
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const decide = (approve: boolean) => {
    setStatus("submitting");
    axiosInstant
      .post("/oauth/consent", { ...Object.fromEntries(params.entries()), approve })
      .then((res) => {
        if (res.data?.redirect) window.location.replace(res.data.redirect);
        else setStatus("error");
      })
      .catch((err) => {
        const httpStatus = err?.response?.status;
        if (httpStatus === 401) return;
        setStatus(failureStatus(httpStatus));
      });
  };

  const who = info?.user?.name || info?.user?.email;

  return (
    <BasicPage>
      <AuthCard elevation={0} variant="outlined">
        <Box textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={3}>
          <Typography variant="h5">Connect {info?.client_name || "an app"}</Typography>
          {(status === "loading" || status === "submitting") && <CircularProgress size={28} />}
          {status === "ready" && info && (
            <>
              <Typography color="text.secondary">
                <strong>{info.client_name}</strong> ({info.redirect_host}) is asking to use the TDC portal as you
                {who ? <> ({who})</> : null}. It will be able to see and change everything you can.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can disconnect it at any time by revoking its key in Settings.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => decide(false)}>
                  Deny
                </Button>
                <Button variant="contained" onClick={() => decide(true)}>
                  Allow
                </Button>
              </Stack>
            </>
          )}
          {(status === "forbidden" || status === "invalid" || status === "error") && (
            <>
              <Typography color="text.secondary">{MESSAGES[status]}</Typography>
              <Button variant="contained" onClick={() => navigate("/", { replace: true })}>
                Back to the portal
              </Button>
            </>
          )}
        </Box>
      </AuthCard>
    </BasicPage>
  );
}
